import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const fmtDate = (d) => new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const LEVELS = [
  { value: 'info', label: 'מידע', color: '#1f6feb', bg: '#eef4ff' },
  { value: 'warning', label: 'דחוף / חשוב', color: '#a06d00', bg: '#fff8e1' },
  { value: 'success', label: 'חדשות טובות', color: '#2e7d32', bg: '#e8f5e9' },
];
const levelMeta = (v) => LEVELS.find((l) => l.value === v) || LEVELS[0];

// תבניות מהירות - ממלאות את הטופס, ניתנות לעריכה לפני שליחה
const TEMPLATES = [
  {
    label: 'הסדר חוב',
    title: 'הסדר חוב מול איגוד',
    body: 'שלום, קיימת יתרת חוב פתוחה מול "איגוד". נשמח להסדיר אותה בהקדם - ניתן לפנות אלינו דרך "צור קשר" באפליקציה.',
    level: 'warning',
  },
  {
    label: 'זכאות לפרסום',
    title: 'מגיע לכם פרסום במסך הראשי!',
    body: 'שימו לב - בית העסק שלכם זכאי לפרסום במסך הראשי של האפליקציה למשך 24 שעות. צרו איתנו קשר כדי לתאם מועד ותוכן.',
    level: 'success',
  },
];

// טאב "הודעות לבתי עסק" - הודעה מהנהלה לבית עסק אחד או יותר. באפליקציה היא
// מוצגת רק לבעלים ולמנהלים של בית העסק (לא לעובדים) - במסך "הודעות מאיגוד"
// ובראש מסך הזנת קוד המימוש. "הסתרה" = active=0 (ההודעה נעלמת מהאפליקציה)
const BusinessMessagesTab = () => {
  const [businesses, setBusinesses] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState('info');
  const [sending, setSending] = useState(false);

  const [filterBusinessId, setFilterBusinessId] = useState('');
  const [toggleBusyId, setToggleBusyId] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [bResp, mResp] = await Promise.all([BOAPI.fetchBusinesses(), BOAPI.fetchBusinessMessages()]);
      if (bResp?.[0]?.[0]?.code === 0) setBusinesses(bResp[1] || []);
      if (mResp?.[0]?.[0]?.code === 0) setMessages(mResp[1] || []);
      else setError(mResp?.[0]?.[0]?.msg || 'שגיאה בטעינת ההודעות');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const reloadMessages = async () => {
    try {
      const resp = await BOAPI.fetchBusinessMessages();
      if (resp?.[0]?.[0]?.code === 0) setMessages(resp[1] || []);
    } catch { /* נשאר עם הרשימה הקודמת */ }
  };

  const filteredBusinesses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter((b) =>
      [b.name, b.city, b.ownerName, b.ownerPhone].some((v) => (v || '').toLowerCase().includes(q)));
  }, [businesses, search]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyTemplate = (t) => {
    setTitle(t.title);
    setBody(t.body);
    setLevel(t.level);
  };

  const send = async () => {
    setError('');
    setSuccess('');
    if (selectedIds.size === 0) { setError('יש לבחור לפחות בית עסק אחד'); return; }
    if (!title.trim() || !body.trim()) { setError('יש למלא כותרת ותוכן'); return; }
    setSending(true);
    try {
      const resp = await BOAPI.sendBusinessMessage({
        businessIds: [...selectedIds],
        title: title.trim(),
        body: body.trim(),
        level,
      });
      if (resp?.[0]?.[0]?.code === 0) {
        const count = resp[1]?.[0]?.sentCount ?? selectedIds.size;
        setSuccess(`ההודעה נשלחה ל-${count} בתי עסק`);
        setSelectedIds(new Set());
        setTitle('');
        setBody('');
        setLevel('info');
        reloadMessages();
      } else {
        setError(resp?.[0]?.[0]?.msg || 'השליחה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSending(false);
    }
  };

  const toggleActive = async (m) => {
    setToggleBusyId(m.id);
    try {
      const resp = await BOAPI.toggleBusinessMessageActive(m.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const active = !!resp[1]?.[0]?.active;
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, active } : x)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setToggleBusyId(null);
    }
  };

  const visibleMessages = filterBusinessId
    ? messages.filter((m) => String(m.businessId) === String(filterBusinessId))
    : messages;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>הודעות לבתי עסק</h2>
        <span style={s.totalBadge}>{messages.length} הודעות</span>
      </div>
      <p style={s.hint}>
        ההודעה תוצג באפליקציה לבעלים ולמנהלים של בית העסק בלבד (לא לעובדים) - במסך "הודעות מאיגוד" ובראש מסך הזנת קוד המימוש.
      </p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeBtn} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {success && (
        <div style={s.successBanner}>✅ {success}
          <button style={{ ...s.closeBtn, color: '#2e7d32' }} onClick={() => setSuccess('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : (
        <div className="bm-grid" style={s.grid}>
          <style>{`@media (max-width: 1000px) { .bm-grid { grid-template-columns: 1fr !important; } }`}</style>

          {/* ── טופס שליחה ── */}
          <div style={s.panel}>
            <h3 style={s.panelTitle}>הודעה חדשה</h3>

            <label style={s.label}>בתי עסק ({selectedIds.size} נבחרו)</label>
            <input
              style={s.input}
              placeholder="חיפוש לפי שם עסק / עיר / בעלים / טלפון"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={s.businessList}>
              {filteredBusinesses.length === 0 ? (
                <div style={s.emptySmall}>לא נמצאו בתי עסק</div>
              ) : filteredBusinesses.map((b) => (
                <label key={b.id} style={{ ...s.businessRow, ...(selectedIds.has(b.id) ? s.businessRowSelected : {}) }}>
                  <input type="checkbox" checked={selectedIds.has(b.id)} onChange={() => toggleSelected(b.id)} />
                  <span style={s.businessName}>{b.name}</span>
                  <span style={s.businessMeta}>
                    {b.city || ''}{b.ownerName ? ` · ${b.ownerName}` : ' · ללא בעלים'}
                  </span>
                </label>
              ))}
            </div>
            {selectedIds.size > 0 && (
              <button style={s.linkBtn} onClick={() => setSelectedIds(new Set())}>ניקוי בחירה</button>
            )}

            <label style={s.label}>תבנית מהירה</label>
            <div style={s.chipsRow}>
              {TEMPLATES.map((t) => (
                <button key={t.label} style={s.templateBtn} onClick={() => applyTemplate(t)}>{t.label}</button>
              ))}
            </div>

            <label style={s.label}>סוג</label>
            <div style={s.chipsRow}>
              {LEVELS.map((l) => (
                <button
                  key={l.value}
                  onClick={() => setLevel(l.value)}
                  style={{
                    ...s.levelChip,
                    ...(level === l.value ? { background: l.bg, color: l.color, borderColor: l.color } : {}),
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <label style={s.label}>כותרת</label>
            <input style={s.input} maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />

            <label style={s.label}>תוכן</label>
            <textarea style={s.textarea} maxLength={2000} rows={5} value={body} onChange={(e) => setBody(e.target.value)} />

            <button style={{ ...s.sendBtn, ...(sending ? { opacity: 0.6 } : {}) }} onClick={send} disabled={sending}>
              {sending ? 'שולח...' : `שליחה${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
            </button>
          </div>

          {/* ── היסטוריה ── */}
          <div style={s.panel}>
            <div style={s.historyHeader}>
              <h3 style={{ ...s.panelTitle, margin: 0 }}>הודעות שנשלחו</h3>
              <select style={s.select} value={filterBusinessId} onChange={(e) => setFilterBusinessId(e.target.value)}>
                <option value="">כל בתי העסק</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            {visibleMessages.length === 0 ? (
              <div style={s.emptySmall}>עדיין לא נשלחו הודעות</div>
            ) : (
              <div style={s.list}>
                {visibleMessages.map((m) => {
                  const meta = levelMeta(m.level);
                  return (
                    <div key={m.id} style={{ ...s.card, borderRight: `4px solid ${meta.color}`, ...(m.active ? {} : s.cardHidden) }}>
                      <div style={s.cardHeader}>
                        <span style={s.cardBusiness}>🏪 {m.businessName}</span>
                        <span style={s.date}>{fmtDate(m.iTime)}</span>
                      </div>
                      <div style={s.cardTitle}>{m.title}</div>
                      <p style={s.cardBody}>{m.body}</p>
                      <div style={s.cardFooter}>
                        <span style={{ ...s.badge, background: meta.bg, color: meta.color }}>{meta.label}</span>
                        <span style={s.readInfo}>נקראה ע"י {m.readCount} מתוך {m.recipientsCount}</span>
                        {!m.active && <span style={{ ...s.badge, background: '#f1f3f5', color: '#697386' }}>מוסתרת</span>}
                        <button
                          style={m.active ? s.hideBtn : s.showBtn}
                          onClick={() => toggleActive(m)}
                          disabled={toggleBusyId === m.id}
                        >
                          {toggleBusyId === m.id ? '...' : m.active ? 'הסתרה' : 'הצגה מחדש'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container:     { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:         { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:    { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  hint:          { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  center:        { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  successBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e8f5e9', color: '#2e7d32', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeBtn:      { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },

  grid:          { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' },
  panel:         { border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column' },
  panelTitle:    { margin: '0 0 12px 0', color: '#1a1f36', fontSize: 17 },
  label:         { fontSize: 13, fontWeight: 700, color: '#4f566b', margin: '12px 0 6px' },
  input:         { padding: '9px 12px', border: '1px solid #d8dee6', borderRadius: 8, fontSize: 14, direction: 'rtl' },
  textarea:      { padding: '9px 12px', border: '1px solid #d8dee6', borderRadius: 8, fontSize: 14, direction: 'rtl', resize: 'vertical', fontFamily: 'inherit' },
  select:        { padding: '7px 10px', border: '1px solid #d8dee6', borderRadius: 8, fontSize: 13, direction: 'rtl', maxWidth: 220 },

  businessList:  { marginTop: 8, maxHeight: 240, overflowY: 'auto', border: '1px solid #eef0f3', borderRadius: 8 },
  businessRow:   { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 13.5 },
  businessRowSelected: { background: '#eef4ff' },
  businessName:  { fontWeight: 700, color: '#1a1f36' },
  businessMeta:  { color: '#8792a2', fontSize: 12 },
  linkBtn:       { alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#1f6feb', cursor: 'pointer', fontSize: 12.5, padding: '6px 0' },

  chipsRow:      { display: 'flex', gap: 8, flexWrap: 'wrap' },
  templateBtn:   { padding: '6px 12px', background: '#f6f8fb', border: '1px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#4f566b' },
  levelChip:     { padding: '6px 14px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#697386' },
  sendBtn:       { marginTop: 16, padding: '11px 0', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 },

  historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  emptySmall:    { padding: 20, textAlign: 'center', color: '#8792a2', fontSize: 13 },
  list:          { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 640, overflowY: 'auto' },
  card:          { border: '1px solid #e3e8ee', borderRadius: 10, padding: 12, background: '#fff' },
  cardHidden:    { opacity: 0.55, background: '#fafbfc' },
  cardHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  cardBusiness:  { fontWeight: 700, color: '#1a1f36', fontSize: 13 },
  date:          { color: '#8792a2', fontSize: 12 },
  cardTitle:     { fontWeight: 700, color: '#1a1f36', fontSize: 14.5, marginBottom: 4 },
  cardBody:      { color: '#4f566b', fontSize: 13, lineHeight: 1.6, margin: '0 0 10px 0', whiteSpace: 'pre-wrap' },
  cardFooter:    { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  badge:         { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  readInfo:      { color: '#8792a2', fontSize: 12, flex: 1 },
  hideBtn:       { padding: '5px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  showBtn:       { padding: '5px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};

export default BusinessMessagesTab;
